import constate from 'constate'
import { useCallback } from 'react'
import { useQueryClient } from 'react-query'
import { useDispatch } from 'react-redux'
import {
  PaginatedSearchState,
  PaginatedSearchStore,
  usePaginatedSearch,
} from 'scenes/Dashboard/components-next/PaginatedSearch'
import { deleteEntityFromCache, deleteEntityFromListCache } from 'utils-next/api'
import { PaginationOptions } from 'utils-next/types'

import { CORPORATE_CACHE_STORE_ID } from '../../../../../../entities-next/corporate'
import {
  getApiScoutingCampaignHandlers,
  SCOUTING_CAMPAIGN_CACHE_STORE_ID,
  ScoutingCampaign,
  ScoutingCampaignListFilters,
} from '../../../../../../entities-next/scoutingCampaign'
import { showDeleteScoutingCampaignModal } from '../../modals'
import { failedDeleteAlertAction, successfulDeleteAlertAction } from './ScoutingCampaignListViewAlerts.component'

export type ScoutingCampaignListViewStore = {
  state: PaginatedSearchStore<ScoutingCampaign, ScoutingCampaignListViewFilters>['state']
  actions: PaginatedSearchStore<ScoutingCampaign, ScoutingCampaignListViewFilters>['actions'] & {
    deleteScoutingCampaign: (scoutingCampaign: ScoutingCampaign) => Promise<void>
  }
}

export type ScoutingCampaignListViewFilters = ScoutingCampaignListFilters

function useScoutingCampaignListViewInternalStore(): ScoutingCampaignListViewStore {
  const { state, actions: paginatedSearchActions } = usePaginatedSearch<
    ScoutingCampaign,
    ScoutingCampaignListViewFilters
  >(SCOUTING_CAMPAIGN_CACHE_STORE_ID, scoutingCampaignListQuery, {
    initialFilters: {},
  })
  const scoutingCampaignListViewActions = useScoutingCampaignListViewActions(state)
  return {
    state,
    actions: {
      ...paginatedSearchActions,
      ...scoutingCampaignListViewActions,
    },
  }
}

function useScoutingCampaignListViewActions(
  state: PaginatedSearchState<ScoutingCampaign, ScoutingCampaignListViewFilters>
) {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const deleteScoutingCampaign = useCallback(
    async (scoutingCampaign: ScoutingCampaign) => {
      const { apiDeleteScoutingCampaign } = getApiScoutingCampaignHandlers()
      const shouldDelete = await showDeleteScoutingCampaignModal({ scoutingCampaign })
      if (shouldDelete) {
        const queryKey = [
          SCOUTING_CAMPAIGN_CACHE_STORE_ID,
          { ...state.filters },
          {
            currentPage: state.currentPage,
            limit: state.limit,
          },
        ]
        apiDeleteScoutingCampaign(scoutingCampaign.id)
          .then(() => {
            deleteEntityFromListCache(queryClient, scoutingCampaign.id, queryKey)
            deleteEntityFromCache(queryClient, scoutingCampaign.id, CORPORATE_CACHE_STORE_ID)
            dispatch(successfulDeleteAlertAction(scoutingCampaign))
          })
          .catch(() => {
            dispatch(failedDeleteAlertAction(scoutingCampaign))
          })
      }
    },
    [dispatch]
  )

  return {
    deleteScoutingCampaign,
  }
}

async function scoutingCampaignListQuery(
  _key: string,
  filters: ScoutingCampaignListViewFilters,
  paginationOptions: PaginationOptions
) {
  const { apiGetScoutingCampaignList } = getApiScoutingCampaignHandlers()
  const query = await apiGetScoutingCampaignList(filters, paginationOptions)
  return query.results
}

export const [ScoutingCampaignListViewProvider, useScoutingCampaignListViewStore] = constate(
  useScoutingCampaignListViewInternalStore
)
